/* Type for the object that will be passed to validate function as the first parameter */
export type InputObjectType = {
    name: string;
    value: string;
};

/*
    Function signature for callback function. 
    This function will accept object of type 'InputObjectType' and return a string which will be an error message
*/
export type CallbackType = (input: InputObjectType) => string;

/* Type for the different validation constraints, label property is mandatory while others are optional */
export type ValidatorRuleType = {
    required?: boolean;
    minLength?: number;
    maxLength?: number;
    regex?: RegExp;
    customValidation?: boolean;
    requiredMsg?: string;
    minLengthMsg?: string;
    maxLengthMsg?: string;
    regexMsg?: string;
    label: string;
    placeholder?: string;
    options?: {
        optLabel: string,
        value: string,
    }[],
};

/* Type for the error object */
export type ErrorObjectType = {
    errorCode: string | undefined; // optional
    errorMsg: string | undefined;
};

type ValidatorTypes = boolean | string | number | RegExp;

/* Utility function to check the empty value. */
export const isEmpty = (value: string) => value.trim().length <= 0;

/* 
    This function is reponsibled to handle the password validation.
    This accepts 2 arguments, password and confirmPassword and return undefined if data is valid.
    Invalid data will return error object which will of type  ErrorObjectType
*/
export const validatePasswordHandler = (pwd: string, confirmPwd: string) => {
    if (pwd === "") {
        return {
            errorCode: "required",
            errorMsg: "Please Enter Password.",
        };
    }
    if (pwd.length < 8) {
        return {
            errorCode: "passwordnotmatch",
            errorMsg: "Password should have at least 8 characters.",
        };
    }
    if (!/^[^:%&?]+$/.test(pwd)) {
        return {
            errorCode: "passwordspecialcharacter",
            errorMsg: "Password should not contain '%' ,'?', '&' and ':' special characters.",
        };
    }
    if (!/^[^\s].+[^\s]$/.test(pwd)) {
        return {
            errorCode: "passwordspecialcharacter",
            errorMsg: "Please enter valid password.",
        };
    }
    if (!!pwd && !!confirmPwd && pwd !== confirmPwd) {
        return {
            errorCode: "passwordnotmatch",
            errorMsg: "Password does not match with Password Confirmation.",
        };
    }
};

/* 
    This function is reponsibled to handle the password validation.
    This accepts 2 arguments, password and confirmPassword and return undefined if data is valid.
    Invalid data will return error object which will of type  ErrorObjectType
*/
export const validateConfirmPasswordHandler = (pwd: string, confirmPwd: string) => {
    if (confirmPwd === "") {
        return {
            errorCode: "required",
            errorMsg: "Please Enter Password Confirmation.",
        };
    }
    if (!!pwd && !!confirmPwd && pwd !== confirmPwd) {
        return {
            errorCode: "passwordnotmatch",
            errorMsg: "Password does not match with Password Confirmation.",
        };
    }
};

/*
    This method will accept the input/select element as the first parameter which is mandatory. Second
    parameter is optional which accepts a callback function. This callback function should be passed only
    when custom validation is required. Callback function should return an empty string when the validation
    pass else throws an error message.
*/
export const validate = (
    input: InputObjectType,
    validatorConfig: ValidatorRuleType,
    callback?: CallbackType,
): ErrorObjectType | undefined => {
    if (!input || (input && !input.name)) {
        return;
    }

    const configObject = validatorConfig;
    const errorLabel: string = configObject.label;
    let isError: ErrorObjectType | undefined;

    if (callback) {
        configObject.customValidation = true;
    }

    const skipValidations = ["label", "placeholder", "requiredMsg", "minLengthMsg", "maxLengthMsg", "customMsg", "options"];

    Object.keys(configObject).every(key => {
        if (skipValidations.indexOf(key) === -1) {
            isError = validateField(
                key,
                <ValidatorTypes>configObject[key as keyof ValidatorRuleType],
                input,
                <CallbackType>callback,
                errorLabel,
                configObject,
            );

            if (isError !== undefined) {
                return false;
            }
        }
        return true;
    });

    return isError === undefined ? undefined : isError;
};

/*
    This function will accept the constraint type, constraint value, inputObject and callback function
    Invalid data will return error object which will of type  ErrorObjectType.
    Valid data will return undefined.
*/
const validateField = (
    validationRule: string,
    validationValue: ValidatorTypes,
    input: InputObjectType,
    callback: CallbackType,
    errorLabel: string,
    configObject: ValidatorRuleType,
) => {
    const errorObject: ErrorObjectType = {
        errorCode: undefined,
        errorMsg: undefined,
    };

    switch (validationRule) {
        case "required": {
            if (isEmpty(input.value)) {
                errorObject.errorCode = validationRule;
                errorObject.errorMsg = configObject.requiredMsg
                    ? configObject.requiredMsg
                    : `Please Enter ${errorLabel}.`;
            }
            break;
        }
        case "minLength": {
            if (String(input.value.length) < validationValue) {
                errorObject.errorCode = validationRule;
                errorObject.errorMsg = configObject.minLengthMsg
                    ? configObject.minLengthMsg
                    : `${errorLabel} should contain at least ${validationValue} characters.`;
            }
            break;
        }
        case "maxLength": {
            if (String(input.value.length) < validationValue) {
                errorObject.errorCode = validationRule;
                errorObject.errorMsg = configObject.maxLengthMsg
                    ? configObject.maxLengthMsg
                    : `${errorLabel} should contain at least ${validationValue} characters.`;
            }
            break;
        }
        case "regex": {
            if (!(<RegExp>validationValue).test(input.value)) {
                errorObject.errorCode = validationRule;
                errorObject.errorMsg = configObject.regexMsg
                    ? configObject.regexMsg
                    : `Please Enter Valid ${errorLabel}.`;
            }
            break;
        }
        case "customValidation": {
            if (callback) {
                const validationMsg = callback({ name: input.name, value: input.value });
                if (validationMsg) {
                    errorObject.errorCode = `customValidation`;
                    errorObject.errorMsg = validationMsg;
                }
            }
            break;
        }
        default: {
            break;
        }
    }

    return errorObject.errorCode === undefined ? undefined : errorObject;
};
